/**
 * Portal: ask for the link that lets a patron choose their password, to
 * activate their account or replace a forgotten password.
 */

import { useId, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { MailCheck } from "lucide-react";
import { requestLink } from "../account-access/accountAccessApi";

const inputClass =
  "w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white";

export function AccountAccessRequestPage() {
  const [searchParams] = useSearchParams();
  const activation = searchParams.has("activation");
  const id = useId();

  const [identifier, setIdentifier] = useState("");
  const [pending, setPending] = useState(false);
  const [sent, setSent] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!identifier.trim()) {
      setError("Saisissez votre numéro de carte ou votre adresse électronique.");
      return;
    }

    setError(null);
    setPending(true);
    try {
      setSent((await requestLink(identifier.trim())).message);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Une erreur est survenue.");
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <div className="w-full max-w-md rounded-xl border border-gray-200 bg-white p-8 shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          {activation ? "Activer votre compte" : "Mot de passe oublié"}
        </h1>

        {sent ? (
          <div role="status" className="mt-6 space-y-4">
            <p className="flex gap-3 rounded-lg bg-emerald-50 p-4 text-sm text-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-300">
              <MailCheck className="h-5 w-5 shrink-0" aria-hidden />
              {sent}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Rien reçu après quelques minutes ? Vérifiez vos courriers indésirables. Si votre
              bibliothèque n&apos;a pas votre adresse électronique, adressez-vous à l&apos;accueil.
            </p>
          </div>
        ) : (
          <>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              {activation
                ? "Pour votre première connexion, nous vous envoyons un lien pour choisir votre mot de passe."
                : "Nous vous envoyons un lien pour choisir un nouveau mot de passe."}{" "}
              Il arrive à l&apos;adresse électronique connue de la bibliothèque.
            </p>

            <form onSubmit={(e) => void submit(e)} noValidate className="mt-6 space-y-4">
              <div>
                <label
                  htmlFor={`${id}-identifier`}
                  className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Numéro de carte ou adresse électronique
                </label>
                <input
                  id={`${id}-identifier`}
                  value={identifier}
                  onChange={(e) => {
                    setIdentifier(e.target.value);
                  }}
                  autoComplete="username"
                  autoFocus
                  required
                  aria-invalid={error ? "true" : undefined}
                  aria-describedby={error ? `${id}-error` : undefined}
                  className={inputClass}
                />
              </div>

              {error && (
                <p
                  id={`${id}-error`}
                  role="alert"
                  className="rounded-lg bg-red-100 p-3 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-400"
                >
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={pending}
                className="w-full rounded-lg bg-blue-600 px-4 py-3 font-medium text-white hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-none disabled:cursor-wait disabled:opacity-70 dark:focus:ring-offset-gray-800"
              >
                {pending ? "Envoi…" : "Recevoir le lien"}
              </button>
            </form>
          </>
        )}

        <p className="mt-6 text-center text-sm">
          <Link
            to="/opac/login"
            className="rounded text-blue-600 hover:text-blue-700 focus:ring-2 focus:ring-blue-500 focus:outline-none dark:text-blue-400"
          >
            ← Retour à la connexion
          </Link>
        </p>
      </div>
    </div>
  );
}

export default AccountAccessRequestPage;
